import { useState } from "react";
import { Copy, Check, IdCard, Users, UserCog } from "lucide-react";
import { ApplicantStatusBadges } from "./ApplicantCard.jsx";
import PassportDocumentViewer from "./PassportDocumentViewer.jsx";
import {
  formatDateTh,
  resolveApplicantSteps,
  describeEkycProcessStatus,
  describeInviteSourceStatus,
  describeTrackingStatus,
  LINE_MEMBERSHIP_COPY,
} from "./applicantShared.js";
import { copyTextToClipboard } from "./clipboard.js";
import { BuddyManagerSection } from "./buddyGuarantees/BuddyGuaranteeDrawerContent.jsx";
import WorkerDetailedProfileDialog from "../workerDetailedProfile/WorkerDetailedProfileDialog.jsx";
import "./buddyGuarantees/buddyGuarantees.css";

import { DetailSkeleton } from "../components/Skeleton.jsx";
/**
 * P157: a small inline "copy this value" control. Backoffice operators read
 * these numbers off the screen to paste into other systems (WhatsApp, an e-KYC
 * tool), so every real identifier - phone, passport number, application id -
 * gets one rather than forcing a manual select-drag on a drawer that scrolls.
 */
function CopyChip({ value, label }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    const ok = await copyTextToClipboard(value);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }

  return (
    <button
      type="button"
      className="wx-applicant-copy-chip"
      onClick={handleCopy}
      aria-label={`คัดลอก${label ? ` ${label}` : ""}`}
      title="คัดลอก"
    >
      {copied ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
    </button>
  );
}

function describeDetailError(error) {
  if (error?.status === 404) {
    return "ไม่พบใบสมัครรายนี้";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ดูใบสมัครรายนี้";
  }
  return "ไม่สามารถโหลดรายละเอียดได้ กรุณาลองใหม่อีกครั้ง";
}

/**
 * Prompt 138Y-B: the drawer's new headline - a calm, factual restatement of
 * a fact the backend already persisted at quick-register time
 * (WmxWorkerApplications.AgentId is set the moment the row is created,
 * confirmed against the schema/service source during the Prompt 138Y-A
 * audit). Deliberately not a "success" panel - no green, no checkmark
 * icon, no button - because nothing is being confirmed by this render; it
 * only restates an already-true, already-persisted fact. There is no
 * backend column or endpoint for a distinct Agent "acceptance" event today
 * (the audit's own gap finding), so this never pretends to be a clickable
 * confirm action.
 */
function ApplicantMembershipPanel({ detail }) {
  const submittedAt = formatDateTh(detail.createdAtUtc);
  return (
    <section className="wx-applicant-membership-panel">
      <span className="wx-applicant-membership-heading">{LINE_MEMBERSHIP_COPY.heading}</span>
      <p className="wx-applicant-membership-body">{LINE_MEMBERSHIP_COPY.body}</p>
      {submittedAt && (
        <div className="wx-applicant-membership-meta">
          <span>วันที่เข้าสายงาน</span>
          <strong>{submittedAt}</strong>
        </div>
      )}
    </section>
  );
}

/**
 * Prompt 138W-R5: the drawer's status stepper - now 5 steps (Prompt
 * 138Y-B): Invite Card submission and line membership are both
 * unconditionally "done" (see resolveApplicantSteps's own doc comment for
 * why), e-KYC/Worker Profile/Ready For Match stay dynamic/locked exactly as
 * before. Neutral/blue/amber only, never red - even an e-KYC rejection
 * renders as a filled amber dot, not a red one, since this is a status
 * readout, not an error banner.
 */
function ApplicantStatusStepper({ applicationStatus }) {
  const steps = resolveApplicantSteps(applicationStatus);
  return (
    <ol className="wx-applicant-stepper">
      {steps.map((step) => (
        <li key={step.key} className={`wx-applicant-step wx-applicant-step--${step.state}`}>
          <span className="wx-applicant-step-dot" aria-hidden="true" />
          <span className="wx-applicant-step-label">{step.label}</span>
          <span className="wx-applicant-step-state">
            {step.state === "done" && "เสร็จแล้ว"}
            {step.state === "active" && "กำลังดำเนินการ"}
            {step.state === "attention" && "ต้องดำเนินการ"}
            {step.state === "waiting" && "รอดำเนินการ"}
            {step.state === "pending" && "ยังไม่เริ่ม"}
            {step.state === "locked" && "ล็อก"}
          </span>
        </li>
      ))}
    </ol>
  );
}

/**
 * P157: the identity / passport card, redesigned.
 *
 * Two things changed from the Prompt 139B version an operator complained about:
 *   1. The passport NUMBER is shown in FULL (detail.passportNumber), not masked
 *      to "****1234". This is a backoffice screen behind the same visibility
 *      check that already streams the document image - masking the text while
 *      showing the picture of it was pure theatre. Falls back to the masked
 *      value only for an old record the detail API has not caught up on.
 *   2. The document is shown as the REAL IMAGE (an inline thumbnail that
 *      enlarges), not as a stored file name that read like a path and could
 *      not actually be used to verify anyone.
 *
 * Renders nothing for an applicant that predates these fields entirely (no
 * number and no document) rather than a misleading "no passport" claim.
 */
function ApplicantPassportSection({ detail, workerName }) {
  const number = detail.passportNumber || detail.passportNumberMasked;
  if (!number && !detail.hasPassportDocument) {
    return null;
  }

  return (
    <section className="wx-applicant-dsec wx-applicant-id-card">
      <div className="wx-applicant-dsec-head">
        <IdCard size={16} aria-hidden="true" />
        <h3>เอกสารประจำตัว</h3>
      </div>

      <div className="wx-applicant-id-body">
        <div className="wx-applicant-id-fields">
          <div className="wx-applicant-id-field">
            <span className="wx-applicant-id-field-label">หมายเลข Passport</span>
            {number ? (
              <span className="wx-applicant-id-number">
                <strong>{number}</strong>
                <CopyChip value={number} label="หมายเลข Passport" />
              </span>
            ) : (
              <strong className="wx-applicant-id-muted">ยังไม่มีหมายเลข</strong>
            )}
          </div>
          {detail.hasPassportDocument && detail.passportDocument?.uploadedAtUtc && (
            <div className="wx-applicant-id-field">
              <span className="wx-applicant-id-field-label">อัปโหลดเมื่อ</span>
              <strong>{formatDateTh(detail.passportDocument.uploadedAtUtc) ?? "-"}</strong>
            </div>
          )}
        </div>

        <PassportDocumentViewer
          applicationId={detail.applicationId}
          workerName={workerName}
          hasDocument={detail.hasPassportDocument}
        />
      </div>
    </section>
  );
}

/**
 * P146: the "fill in this worker's detailed profile" entry point.
 *
 * Quick Register asks for almost nothing so a worker can sign up fast, which
 * leaves every field a job match actually needs (date of birth, passport
 * expiry, experience, languages, workable provinces, availability, ...) with
 * nowhere to live until someone in the backoffice enters it. This section is
 * that door, and it is placed directly under the applicant's own data rather
 * than at the bottom of the drawer, because it is the single most common
 * thing an Agent has to do to a freshly Quick Registered worker.
 *
 * Opens a dialog over this drawer rather than navigating: the operator is
 * already looking at this exact worker, and a top-level page change mid-task
 * reads as a wrong turn (the P145 rule). The dialog itself reads the real
 * canEdit flag from the backend and falls back to a read-only view - this
 * button is offered to anyone who can see the applicant, and permission is
 * decided by the server, not by hiding the button.
 */
function ApplicantDetailedProfileSection({ applicationId, onSaved }) {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <section className="wx-applicant-dsec">
      {/* P157: was a bare heading + paragraph + a stray full-width button that
          read as untidy for what is the single most common action on a fresh
          Quick Registered worker. Now one self-contained call-to-action card:
          icon, title, one-line why, and the action aligned to the right (it
          drops below the text on a narrow drawer). */}
      <div className="wx-applicant-profile-cta">
        <span className="wx-applicant-profile-cta-icon" aria-hidden="true">
          <UserCog size={20} />
        </span>
        <div className="wx-applicant-profile-cta-text">
          <strong>โปรไฟล์แรงงานแบบละเอียด</strong>
          <span>ข้อมูลจากการสมัครเร็วยังไม่พอสำหรับการจับคู่งาน ดูและแก้ไขข้อมูลตัวตน เอกสาร ทักษะ และพื้นที่ที่ทำงานได้ที่นี่</span>
        </div>
        {/* P157-R3: the label says ดู, not เติม - the dialog behind it is a full
            view of the record that happens to be editable (and falls back to a
            read-only view when the backend's own canEdit says so), so a
            fill-in-the-blanks label mis-sold it for an already-complete worker. */}
        <button
          type="button"
          className="wx-button wx-button--primary wx-applicant-profile-cta-button"
          onClick={() => setDialogOpen(true)}
        >
          ดูโปรไฟล์แรงงานแบบละเอียด
        </button>
      </div>
      <WorkerDetailedProfileDialog
        open={dialogOpen}
        applicationId={applicationId}
        onClose={() => setDialogOpen(false)}
        onSaved={onSaved}
      />
    </section>
  );
}

/**
 * Prompt 138Y-D: e-KYC is now status-only, never an action - the Agent
 * cannot and should not start e-KYC themselves (the flow is Quick Register
 * → already in the Agent's line → central e-KYC → central/backoffice
 * creates the Worker Profile). Prompt 138W-R5's real POST .../ekyc/start
 * wiring and Prompt 138Y-B's demoted secondary button are both removed
 * outright, not just hidden - this component has no submit state, no
 * onClick, no import of startEkyc. The backend endpoint itself
 * (EkycReviewService.StartEkycAsync) is untouched and still real; the
 * SuperAdmin-facing Worker Application Inbox keeps its own separate,
 * already-disabled placeholder button unchanged - this file simply never
 * offers any Agent-facing path to that action anymore.
 */
function ApplicantEkycStatus({ applicationStatus }) {
  return (
    <section className="wx-applicant-dsec">
      <h3>สถานะ e-KYC</h3>
      <p className="wx-applicant-ekyc-status-text">{describeEkycProcessStatus(applicationStatus)}</p>
    </section>
  );
}

/**
 * Prompt 140B-R2 added a real Buddy count here; Prompt 140B-R3 makes the
 * add/edit/revoke actions themselves work right here too - a "จัดการ Buddy"
 * link that only ever navigated away used to be the sole way to act on
 * that count, forcing a full page change for a single-buddy edit. Reuses
 * `BuddyManagerSection` (factored out of the Buddy Guarantee page's own
 * `PersonDetailView` for exactly this reuse) - the same real
 * useSourcePersonBuddies/createBuddyGuarantee/revokeBuddyGuarantee calls,
 * same 0/5 cap behavior, same honest "..." while loading. The Buddy
 * Guarantee page itself is kept as a secondary link below - real for bulk
 * management across many people at once, never the only place a single
 * buddy can be managed.
 */
function ApplicantBuddySection({ applicationId, name, pipelineStatus }) {
  const source = { sourceType: "WorkerApplication", workerApplicationId: applicationId, workerProfileId: null, name, pipelineStatus };
  return (
    <section className="wx-applicant-dsec">
      <div className="wx-applicant-dsec-head">
        <Users size={16} aria-hidden="true" />
        <h3>Buddy (เพื่อนคู่ดูแล)</h3>
      </div>
      {/* P157: one plain line saying what Buddy IS, because the section landed
          cold before with no context and read as confusing. The heavy network
          diagram is dropped here (showNetworkChart={false}) - the list plus
          add/revoke is the actionable part; the diagram lives on the full
          Buddy Guarantee page linked below. */}
      <p className="wx-applicant-buddy-intro">
        จับคู่ผู้สมัครรายนี้กับแรงงานอีกคนในสายงานเดียวกัน เพื่อใช้เป็นผู้ติดต่อยามฉุกเฉิน (สูงสุด 5 คน) — ไม่มีการส่งแจ้งเตือนจริงถึงใคร
      </p>
      <BuddyManagerSection source={source} showNetworkChart={false} formPresentation="dialog" />
      {/* P157: the "ดูแผนผัง Buddy ทั้งหมด" bulk link was removed at the owner's
          request - buddies are managed inline right here, and the full network
          page is still reachable from the sidebar's Buddy Guarantee entry. */}
    </section>
  );
}

/**
 * Prompt 140B-R2: device binding/mobile tracking only ever exists once a
 * real job Assignment begins onboarding (AssignmentOnboardingService) -
 * there is no real per-applicant tracking field to read at this stage, so
 * this is a fixed, honest "not started yet" status, never a computed field
 * and never implied by (or gated on) Ready for Match.
 */
function ApplicantTrackingSection() {
  return (
    <section className="wx-applicant-dsec">
      <h3>Tracking</h3>
      <p className="wx-applicant-next-action-text">{describeTrackingStatus()}</p>
    </section>
  );
}

/**
 * Prompt 138W-R4/R5, rewritten by Prompt 138Y-B, finalized by Prompt 138Y-D:
 * the shared applicant detail drawer body - reused by AgentApplicationsPage,
 * AgentWorkersPage, AgentOrgChartPage, and the Agent Dashboard's own
 * applicant card list, so "click an applicant" means the exact same real
 * detail everywhere.
 *
 * Domain rule (unchanged since 138W-R2): never renders a workerId (the DTO
 * has none), never claims "ready for match" - stage.readyForMatch is always
 * false at this point, and the status stepper says so honestly ("locked")
 * instead of silently omitting it.
 *
 * Prompt 138Y-D: the drawer no longer offers any e-KYC action at all - the
 * flow is Quick Register submitted → already in the Agent's line → waiting
 * for central e-KYC → central/backoffice creates the Worker Profile. e-KYC
 * renders as pure status text (ApplicantEkycStatus).
 *
 * P157: the "สิ่งที่ต้องทำต่อ" next-action section and the "ติดต่อผู้สมัคร"
 * copy-phone section were removed at the owner's request - the e-KYC status
 * readout is the honest signal, and the phone carries an inline copy chip, so
 * both read as redundant clutter.
 */
export function ApplicantDetailContent({ titleId, state, onProfileSaved }) {
  if ((state.status === "loading" || state.status === "idle") && !state.data) {
    return (
      <DetailSkeleton label="กำลังโหลดรายละเอียด..." />
    );
  }

  if (state.status === "error" && !state.data) {
    return (
      <div className="wx-applicant-drawer-state" role="alert">
        <h2 id={titleId} className="wx-state-title">
          ไม่สามารถโหลดรายละเอียดได้
        </h2>
        <p className="wx-state-body">{describeDetailError(state.error)}</p>
      </div>
    );
  }

  const detail = state.data;
  const workerName = detail.fullNameNative || detail.fullNameEnglish || "-";

  return (
    <div className="wx-applicant-drawer">
      <header className="wx-applicant-drawer-head">
        <span className="wx-applicant-drawer-kicker">ใบสมัคร Quick Registered</span>
        <h2 id={titleId} className="wx-applicant-drawer-title">
          {workerName}
        </h2>
        {detail.fullNameEnglish && detail.fullNameEnglish !== workerName && (
          <p className="wx-applicant-drawer-subname">{detail.fullNameEnglish}</p>
        )}
        <p className="wx-applicant-drawer-sub">
          <span className="wx-applicant-mono">{detail.applicationId}</span>
          <CopyChip value={detail.applicationId} label="รหัสใบสมัคร" />
        </p>
        <ApplicantStatusBadges item={detail} />
      </header>

      <ApplicantMembershipPanel detail={detail} />

      {/* P157-R3: moved to the top at the owner's request ("เอาไว้ด้านบนครับ").
          It used to sit below the passport card, four sections down. This is the
          single most common thing an operator does to a Quick Registered worker,
          and the worker drawer already put its own copy directly under the
          membership panel - so both drawers now open on the same action. */}
      <ApplicantDetailedProfileSection applicationId={detail.applicationId} onSaved={onProfileSaved} />

      <section className="wx-applicant-dsec">
        <h3>สถานะในสายงาน</h3>
        <ApplicantStatusStepper applicationStatus={detail.applicationStatus} />
      </section>

      <section className="wx-applicant-dsec">
        <h3>ข้อมูลผู้สมัคร</h3>
        <div className="wx-applicant-kv-row">
          <span>ชื่อ (ภาษาอังกฤษ)</span>
          <strong>{detail.fullNameEnglish || "-"}</strong>
        </div>
        <div className="wx-applicant-kv-row">
          <span>สัญชาติ</span>
          <strong>{detail.nationality || "-"}</strong>
        </div>
        <div className="wx-applicant-kv-row">
          <span>เบอร์โทร / WhatsApp</span>
          <strong className="wx-applicant-kv-copyable">
            {detail.phoneNumber || "-"}
            {detail.phoneNumber && <CopyChip value={detail.phoneNumber} label="เบอร์โทร" />}
          </strong>
        </div>
        <div className="wx-applicant-kv-row">
          <span>ภาษาที่ถนัด</span>
          <strong>{detail.preferredLanguageCode || "-"}</strong>
        </div>
        {/* Prompt 139B: only present once the applicant submitted via the
            real Prompt 139A document-submit route - null for anything that
            predates it (the old JSON-only route never sets a skill). */}
        <div className="wx-applicant-kv-row">
          <span>ความถนัด</span>
          <strong>{detail.skill?.name || "-"}</strong>
        </div>
        <div className="wx-applicant-kv-row">
          <span>สร้างเมื่อ</span>
          <strong>{formatDateTh(detail.createdAtUtc) ?? "-"}</strong>
        </div>
      </section>

      <ApplicantPassportSection detail={detail} workerName={workerName} />

      <section className="wx-applicant-dsec">
        <h3>Invite Card / แหล่งที่มา</h3>
        <div className="wx-applicant-kv-row">
          <span>รหัส Invite (masked)</span>
          <strong>{detail.inviteCard?.inviteCodeMasked || "-"}</strong>
        </div>
        <div className="wx-applicant-kv-row">
          <span>กลุ่มเป้าหมาย</span>
          <strong>
            {[detail.inviteCard?.intendedCountryCode, detail.inviteCard?.intendedLanguageCode].filter(Boolean).join(" / ") || "-"}
          </strong>
        </div>
        {detail.inviteCard?.noteToWorker && (
          <div className="wx-applicant-kv-row">
            <span>บันทึกจาก Agent</span>
            <strong>{detail.inviteCard.noteToWorker}</strong>
          </div>
        )}
        {/* Prompt 139C: purely informational - a revoked/expired source
            link is never a reason this applicant disappears from the
            pipeline, so this note always explicitly says so too. */}
        {describeInviteSourceStatus(detail.inviteCard?.sourceStatus) && (
          <p className="wx-applicant-invite-source-note">{describeInviteSourceStatus(detail.inviteCard?.sourceStatus)}</p>
        )}
      </section>

      <ApplicantEkycStatus applicationStatus={detail.applicationStatus} />

      <ApplicantTrackingSection />

      <ApplicantBuddySection
        applicationId={detail.applicationId}
        name={detail.fullNameNative || detail.fullNameEnglish || "-"}
        pipelineStatus={detail.applicationStatus}
      />
      {/* P157: "สิ่งที่ต้องทำต่อ" (ApplicantNextActionSection) and "ติดต่อผู้สมัคร"
          (ApplicantContactAction) were removed at the owner's request - the
          drawer already shows the e-KYC status above, and the phone now has an
          inline copy chip in ข้อมูลผู้สมัคร, so both read as redundant clutter. */}
    </div>
  );
}
